import { useCallback, useEffect, useMemo, useState } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import type { Player, TimeRange } from "@backstory/player";
import { DEFAULT_HALF_MS } from "@backstory/player";
import type { Issue, IssueCluster } from "@backstory/protocol";
import { request } from "../../api";
import { useAuth } from "../../auth/store";
import { formatClock } from "../../lib/format";
import { Skeleton } from "../../components/States";
import { aiApi } from "./api";
import { useAiCapability, useCitationSeek, useDeepAnalysis } from "./hooks";
import { SummaryCard } from "./components/SummaryCard";
import { RangeControl } from "./components/RangeControl";
import { AiBlockedTooltip, AiOutput, AiUnavailableNote, JobStatus, Rejected } from "./components/common";
import { ExplainArtifactView, ReproView } from "./components/artifacts";
import { askWhy } from "./askwhy/store";
import "./ai.css";

const enc = encodeURIComponent;

export interface WhyPanelProps {
  sessionId: string;
  player: Player | null;
  /** Playhead, relative ms. */
  time: number;
  startTs: number;
  seek: (relMs: number) => void;
  issues?: Issue[];
}

/** The "Why" tab: summary, issue explanations, Explain this moment, Repro. */
export function WhyPanel({ sessionId, player, time, startTs, seek, issues }: WhyPanelProps) {
  const auth = useAuth();
  const ai = useAiCapability();
  const cite = useCitationSeek(sessionId, seek, startTs);
  const [range, setRange] = useState<TimeRange | null>(() => player?.selection.get() ?? null);
  const [question, setQuestion] = useState("");

  useEffect(() => {
    if (!player) return;
    setRange(player.selection.get());
    return player.on("selectionchange", ({ range }) => setRange(range));
  }, [player]);

  // Effective range: selection, else ±15 s around the playhead.
  const effective = useMemo<TimeRange>(() => {
    if (range && range.toMs > range.fromMs) return range;
    const d = player?.duration ?? Number.POSITIVE_INFINITY;
    return { fromMs: Math.max(0, Math.round(time - DEFAULT_HALF_MS)), toMs: Math.round(Math.min(d, time + DEFAULT_HALF_MS)) };
  }, [range, time, player]);

  const explain = useDeepAnalysis(useCallback(() => aiApi.explain(sessionId, { fromMs: effective.fromMs, toMs: effective.toMs, question: question.trim() || undefined }), [sessionId, effective.fromMs, effective.toMs, question]));
  const repro = useDeepAnalysis(useCallback(() => aiApi.repro(sessionId, { fromMs: effective.fromMs, toMs: effective.toMs }), [sessionId, effective.fromMs, effective.toMs]));

  // Issues with AI explanations from their clusters.
  const sessionIssues = useQuery({
    queryKey: ["issues", sessionId],
    queryFn: () => request<Issue[] | { items: Issue[] }>(`/v1/sessions/${enc(sessionId)}/issues`).then((r) => (Array.isArray(r) ? r : r.items ?? [])),
    enabled: !issues,
    retry: false,
  });
  const list = issues ?? sessionIssues.data ?? [];
  const clusterIds = useMemo(() => Array.from(new Set(list.map((i) => i.clusterId).filter(Boolean))), [list]);
  const clusters = useQueries({
    queries: clusterIds.map((id) => ({ queryKey: ["insights", "issue", id], queryFn: () => request<IssueCluster>(`/v1/issues/${enc(id)}`), retry: false, staleTime: 60_000 })),
  });
  const clusterById = useMemo(() => {
    const m = new Map<string, IssueCluster>();
    clusters.forEach((q) => q.data && m.set(q.data.id, q.data));
    return m;
  }, [clusters]);

  const orgId = auth.currentOrgId;

  return (
    <div className="why scroll-cue" data-testid="why-panel">
      <SummaryCard sessionId={sessionId} tools={cite} />

      <section aria-labelledby="why-explain">
        <h3 id="why-explain">Explain this moment</h3>
        <RangeControl player={player} time={time} />
        <p className="small muted" style={{ margin: "6px 0" }}>
          {range && range.toMs > range.fromMs ? "Analyzing the selected range." : `No selection: analyzing ±15 s around ${formatClock(time)}.`}
        </p>
        <input className="input" placeholder="Optional question, e.g. why did the upload fail?" value={question} onChange={(e) => setQuestion(e.target.value)} aria-label="Question for the analysis" style={{ width: "100%", marginBottom: 8 }} disabled={ai.blocked} title={ai.titleFor()} autoComplete="off" />
        <div className="actions-row" style={{ alignItems: "center", gap: 10 }}>
          <AiBlockedTooltip ai={ai}>
            <button type="button" className="btn primary" onClick={() => void explain.start()} disabled={explain.busy || ai.blocked} title={ai.titleFor()} data-testid="explain-btn">
              {explain.busy ? "Analyzing…" : explain.state.phase === "done" ? "Re-run" : "Explain"}
            </button>
          </AiBlockedTooltip>
          <AiUnavailableNote ai={ai} />
        </div>
        <div style={{ marginTop: 10 }}>
          {(explain.state.phase === "starting" || explain.state.phase === "running") && <JobStatus job={explain.state.phase === "running" ? explain.state.job : undefined} polls={explain.state.phase === "running" ? explain.state.polls : undefined} />}
          {explain.state.phase === "rejected" && <Rejected job={explain.state.job} orgId={orgId} />}
          {explain.state.phase === "failed" && <div className="error-box" role="alert">{explain.state.error}</div>}
          {explain.state.phase === "done" && <ExplainArtifactView a={explain.state.artifact} tools={cite} />}
        </div>
      </section>

      <section aria-labelledby="why-repro">
        <h3 id="why-repro">Reproduce</h3>
        <p className="small muted" style={{ margin: "0 0 8px" }}>Steps and a Playwright test for the selected range.</p>
        <div className="actions-row" style={{ alignItems: "center", gap: 10 }}>
          <AiBlockedTooltip ai={ai}>
            <button type="button" className="btn" onClick={() => void repro.start()} disabled={repro.busy || ai.blocked} title={ai.titleFor()} data-testid="repro-btn">
              {repro.busy ? "Generating…" : "Generate repro"}
            </button>
          </AiBlockedTooltip>
        </div>
        <div style={{ marginTop: 10 }}>
          {(repro.state.phase === "starting" || repro.state.phase === "running") && <JobStatus job={repro.state.phase === "running" ? repro.state.job : undefined} />}
          {repro.state.phase === "rejected" && <Rejected job={repro.state.job} orgId={orgId} />}
          {repro.state.phase === "failed" && <div className="error-box" role="alert">{repro.state.error}</div>}
          {repro.state.phase === "done" && <ReproView a={repro.state.artifact} />}
        </div>
      </section>

      <section aria-labelledby="why-issues">
        <h3 id="why-issues">Issues in this session</h3>
        {!issues && sessionIssues.isPending ? (
          <Skeleton lines={2} />
        ) : list.length === 0 ? (
          <p className="small muted" style={{ margin: 0 }}>No Signals detected in this session.</p>
        ) : (
          <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 8 }}>
            {list.map((i) => {
              const c = clusterById.get(i.clusterId);
              return (
                <li key={i.id} className="card" style={{ padding: "8px 10px" }} data-testid="why-issue">
                  <div className="actions-row" style={{ alignItems: "center" }}>
                    <span className={`badge ${i.severity === "high" || i.severity === "critical" ? "danger" : i.severity === "medium" ? "warn" : "neutral"}`}>{i.severity}</span>
                    <button type="button" className="btn ghost" onClick={() => seek(i.tStart)} title="Jump to the moment">
                      {c?.title ?? i.kind.replace(/_/g, " ")} · {formatClock(i.tStart)}
                    </button>
                  </div>
                  {c?.aiExplanation ? (
                    <AiOutput className="small" compact>{c.aiExplanation.text}</AiOutput>
                  ) : (
                    <p className="small muted" style={{ margin: "6px 0 0" }}>No cluster explanation yet.</p>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section>
        <AiBlockedTooltip ai={ai}>
          <button type="button" className="btn" onClick={() => askWhy.open({ sessionId, fromMs: effective.fromMs, toMs: effective.toMs, label: `session ${sessionId.slice(0, 8)}…` })} disabled={ai.blocked} title={ai.titleFor()} data-testid="ask-why-open">
            Ask Why about this session
          </button>
        </AiBlockedTooltip>
      </section>
    </div>
  );
}
